// Server-Sent Events over fetch (P1-E05; ARCHITECTURE §4). EventSource cannot send the bearer header, so
// the stream is read by hand:
// - frames are `id:`, `event:`, `data:` (several data lines join with "\n"), `retry:`; `:` lines are
//   comments (the API's `: ping` every 15 s);
// - a reconnect sends `Last-Event-ID`, so the server replays what was missed (or a `snapshot` first when
//   its stream was trimmed past that id);
// - reconnects back off exponentially with jitter (1 s → 30 s), or wait the server's `retry:`;
// - a 401 refreshes the session once and reconnects; a terminal event ends the stream for good.
import { getAccessToken, refreshSession, url } from './auth';

export type SseEvent = { id?: string; event: string; data: unknown };
export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type StreamOptions = {
  path: string;
  onEvent: (e: SseEvent) => void;
  /** After this event the stream is done (a job's terminal event): no reconnect. */
  isTerminal?: (e: SseEvent) => boolean;
  onStatus?: (s: StreamStatus) => void;
  /** Called when the session cannot be refreshed. */
  onUnauthorized?: () => void;
};

export const BACKOFF_MIN_MS = 1000;
export const BACKOFF_MAX_MS = 30_000;

/** The reconnect delay for a 0-based attempt: exponential, capped, with ±50 % jitter downwards. */
export function backoff(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempt);
  return Math.round(base * (0.5 + random() * 0.5));
}

/** An incremental SSE parser: feed it text chunks, it calls `emit` per complete event. */
export function createParser(emit: (e: SseEvent) => void, onRetry: (ms: number) => void = () => undefined) {
  let buffer = '';
  let id: string | undefined;
  let event = '';
  let data: string[] = [];
  const dispatch = () => {
    if (data.length) {
      const raw = data.join('\n');
      let parsed: unknown = raw;
      try {
        parsed = JSON.parse(raw);
      } catch {
        /* a plain-text payload */
      }
      emit({ id, event: event || 'message', data: parsed });
    }
    event = '';
    data = [];
  };
  return (chunk: string) => {
    buffer += chunk;
    const lines = buffer.split(/\r\n|\r|\n/);
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (line === '') {
        dispatch();
        continue;
      }
      if (line.startsWith(':')) continue; // a comment: `: ping`
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
      if (field === 'id') id = value;
      else if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
      else if (field === 'retry' && /^\d+$/.test(value)) onRetry(Number(value));
    }
  };
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });
}

/** Opens a stream that keeps itself connected; returns the function that closes it. */
export function openStream(opts: StreamOptions): () => void {
  const controller = new AbortController();
  const { signal } = controller;
  let lastId: string | undefined;
  let retryMs: number | null = null;
  let done = false;

  // Closing cancels the body being read (rather than passing the signal to fetch, which some runtimes'
  // AbortSignal implementations do not interoperate with).
  let reading: ReadableStreamDefaultReader<Uint8Array> | null = null;
  signal.addEventListener('abort', () => void reading?.cancel().catch(() => undefined));

  void (async () => {
    let attempt = 0;
    let refreshed = false;
    while (!signal.aborted && !done) {
      opts.onStatus?.(attempt === 0 ? 'connecting' : 'reconnecting');
      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        const token = getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        if (lastId) headers['Last-Event-ID'] = lastId;
        const res = await globalThis.fetch(url(opts.path), { headers, credentials: 'include', cache: 'no-store' });
        if (signal.aborted) {
          await res.body?.cancel().catch(() => undefined);
          break;
        }
        if (res.status === 401) {
          if (!refreshed && (await refreshSession())) {
            refreshed = true;
            continue;
          }
          opts.onUnauthorized?.();
          break;
        }
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        refreshed = false;
        attempt = 0;
        opts.onStatus?.('open');
        const reader = res.body.getReader();
        reading = reader;
        const decoder = new TextDecoder();
        const feed = createParser(
          (e) => {
            if (e.id) lastId = e.id;
            if (e.event === 'error') return; // the server's "events unavailable": reconnect below
            opts.onEvent(e);
            if (opts.isTerminal?.(e)) done = true;
          },
          (ms) => {
            retryMs = ms;
          },
        );
        for (;;) {
          const { value, done: end } = await reader.read();
          if (end) break;
          feed(decoder.decode(value, { stream: true }));
          if (done) {
            await reader.cancel().catch(() => undefined);
            break;
          }
        }
        feed('\n\n');
      } catch {
        if (signal.aborted) break;
      }
      if (done || signal.aborted) break;
      await sleep(retryMs ?? backoff(attempt), signal);
      attempt += 1;
    }
    opts.onStatus?.('closed');
  })();

  return () => controller.abort();
}
